import { faCheckCircle, faCircleNotch, faClock, faCopy, faDownload, faExclamation, faPaperclip } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import { Button } from "flowbite-react";
import useToastContext from "hooks/useToastContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { apiGet } from "utils/apiFetch";
import pathObj from "utils/apiPath";

export default function TransactionDetails({ transactionId }) {
  const [loading, setLoading] = useState(true);
  const [transactionData, setTransactionData] = useState({});
  const { t } = useTranslation();
  const notification = useToastContext()

  const getTransactionDetails = async () => {
    try {
      const result = await apiGet(pathObj.transactionDetails, { wallet_transaction_id: transactionId });
      setTransactionData(result.data);
    } catch (err) {}
    setLoading(false);
  };

  useEffect(() => {
    getTransactionDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center w-full h-[10rem]">
        <FontAwesomeIcon icon={faCircleNotch} className="text-xl animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col justify-center items-center px-4">
        <div className="relative">
          <img src={transactionData.senderData?.profile_pic || transactionData.receiverData?.profile_pic || null} className="rounded-full h-[4rem] w-[4rem]" alt="" />
          {transactionData.status === "completed" && <FontAwesomeIcon icon={faCheckCircle} className="absolute right-[-0.2rem] bottom-[-0.2rem] text-green-700" size={"lg"} />}
          {transactionData.status === "pending" && <FontAwesomeIcon icon={faClock} className="absolute right-[-0.2rem] bottom-[-0.2rem] text-yellow-700" size={"lg"} />}
          {transactionData.status === "failed" && <FontAwesomeIcon icon={faExclamation} className="absolute right-[-0.2rem] bottom-[-0.2rem] text-red-700" size={"lg"} />}
        </div>

        <div className="rounded-full bg-[var(--glass)] mt-5 mx-0 px-2 py-1 text-xs">{transactionData.transaction_type}</div>
        <span className={`text-4xl font-bold my-4 ${transactionData.type === "debit" ? "" : "text-green-700"}`}>
          {transactionData.type === "debit" ? "-" : "+"}
          {transactionData.transaction_amount} {transactionData.currency}
        </span>
        <span className="flex justify-start w-full font-medium">{t("O_DETAILS")}</span>
        <div className="grid grid-cols-2 w-full bg-[var(--glass)] font-medium p-4 rounded-xl mt-3">
          <div className="col-span-1">{t("Date")}</div>
          <div className="col-span-1 text-end">{dayjs(transactionData.createdAt).format("DD/MM/YYYY, HH:mm:ss")}</div>
          <div className="col-span-1">{t("TRANSACTION_ID")}</div>
          <div className="col-span-1 text-end gap-2">
            <span>{transactionData.transaction_id}</span>
            <FontAwesomeIcon
              icon={faCopy}
              className="ms-1 cursor-pointer"
              onClick={() => {
                navigator.clipboard.writeText(transactionData.transaction_id).then(() => {
                  notification.success(t("REFERAL_CODE_CLIPBOARD"));
                });
              }}
            />
          </div>
          {transactionData.senderData && (
            <>
              <div className="col-span-1">{t("SENDER")}</div>
              <div className="col-span-1 text-end line-clamp-1">{transactionData.senderData.name}</div>
              {transactionData.senderData.walletData && (
                <>
                  <div className="col-span-1">{t("FROM_WALLET")}</div>
                  <div className="col-span-1 text-end line-clamp-1">{transactionData.senderData.walletData.wallet_number}</div>
                </>
              )}
            </>
          )}
          {transactionData.receiverData && (
            <>
              <div className="col-span-1">{t("RECEIVER")}</div>
              <div className="col-span-1 text-end line-clamp-1">{transactionData.receiverData.name}</div>
              {transactionData.receiverData.walletData && (
                <>
                  <div className="col-span-1">{t("TO_WALLET")}</div>
                  <div className="col-span-1 text-end line-clamp-1">{transactionData.receiverData.walletData.wallet_number}</div>
                </>
              )}
            </>
          )}
          {transactionData.message && (
            <>
              <div className="col-span-1">{t("DESCRIPTION")}</div>
              <div className="col-span-1 text-end">{transactionData.message}</div>
            </>
          )}
          {transactionData.exchange_rate && (
            <>
              <div className="col-span-1">{t("EXCHANGE_RATE")}</div>
              <div className="col-span-1 text-end line-clamp-1">{transactionData.exchange_rate}</div>
            </>
          )}
          {transactionData.original_amount && (
            <>
              <div className="col-span-1">{t("ORIGINAL_AMOUNT")}</div>
              <div className="col-span-1 text-end line-clamp-1">{transactionData.original_amount}</div>
            </>
          )}
          {transactionData.failed_reason && (
            <>
              <div className="col-span-1">{t("FAILED_REASON")}</div>
              <div className="col-span-1 text-end">{transactionData.failed_reason}</div>
            </>
          )}
        </div>

        {transactionData.invoice && (
          <Link to={transactionData.invoice} target="_blank">
            <Button className="mt-5 flex items-center gap-2 bg-primary">
              <FontAwesomeIcon icon={faDownload} size={"lg"} className="card-icon" />
              <span>{t("DOWNLOAD_INVOICE")}</span>
            </Button>
          </Link>
        )}

        {transactionData.attachments.length > 0 && (
          <>
            <span className="flex justify-start w-full font-medium mt-5">{t("O_ATTACHMENTS")}</span>
            {transactionData.attachments.map((it) => (
              <Link to={it} target="_blank">
                <div className="flex justify-start items-center gap-2 bg-[var(--glass)] font-medium p-3 rounded-lg mt-3">
                  <FontAwesomeIcon icon={faPaperclip} className="card-icon" />
                  <span>{it.split("/").pop()}</span>
                </div>
              </Link>
            ))}
          </>
        )}
      </div>
    </>
  );
}
